import { useState, useEffect, useCallback } from 'react';
import { supabase } from './supabaseClient';
import { BILLING_MODELS, MODEL_ORDER, modelLabel, modelBadge } from './billingModels';
import TKTooltip from './TKTooltip';

// Billing terms timeline for one client.
//
// A client's billing is a list of dated periods, not a single rate. That is
// what makes a mid-year rate change, a scheduled increase, and a seasonal
// contract (summer at one rate, the school year at another) the same feature
// rather than three.
//
// Writes go through the RPCs in 20260902_billing_terms_rpcs.sql, never direct
// table writes: closing one period and opening the next has to be one
// transaction or a client can end up with a stretch of time that resolves to
// no terms at all.
//
// Three intents, kept deliberately distinct:
//   Change   the rate genuinely changed as of a date -> new period
//   Correct  the rate was typed wrong -> amend in place, no new period
//   Remove   a scheduled period that should not happen -> predecessor re-extends

// Dates are handled as plain 'YYYY-MM-DD' strings throughout. Passing them
// through Date() would shift them a day in negative UTC offsets.
const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const firstOfThisMonth = () => todayStr().slice(0, 8) + '01';
const firstOfNextMonth = () => {
  const [y, m] = todayStr().split('-').map(Number);
  return m === 12 ? `${y + 1}-01-01` : `${y}-${String(m + 1).padStart(2, '0')}-01`;
};
const prettyDate = (s) => {
  if (!s) return '';
  const [y, m, d] = s.split('-');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[Number(m) - 1]} ${Number(d)}, ${y}`;
};

// effective_to is EXCLUSIVE. Showing the raw value would tell someone their
// summer rate ends Sep 1 when the last day it applies is Aug 31.
const lastCoveredDay = (exclusiveEnd) => {
  if (!exclusiveEnd) return null;
  const [y, m, d] = exclusiveEnd.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d - 1));
  return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, '0')}-${String(dt.getUTCDate()).padStart(2, '0')}`;
};

const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];

const statusOf = (row, today) => {
  if (row.effective_from > today) return 'scheduled';
  if (row.effective_to && row.effective_to <= today) return 'past';
  return 'current';
};

export default function ClientBillingTerms({ clientId, css, C, onError, onChanged }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(null); // { mode:'change'|'correct', id?, model, config, effective_from, note }
  const [confirmRemove, setConfirmRemove] = useState(null); // the row awaiting confirmation
  // Seasonal schedule builder. The pattern is never stored -- it generates
  // concrete periods and is then thrown away. See the migration for why.
  const [season, setSeason] = useState(null);

  const today = todayStr();

  const load = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    const { data, error } = await supabase
      .from('client_billing_terms')
      .select('*')
      .eq('client_id', clientId)
      .order('effective_from', { ascending: true });
    if (error) onError?.('Loading billing terms failed: ' + error.message);
    setRows(data || []);
    setLoading(false);
  }, [clientId, onError]);

  useEffect(() => { load(); }, [load]);

  const openChange = () => {
    const current = rows.find((r) => statusOf(r, today) === 'current') || rows[rows.length - 1];
    setForm({
      mode: 'change',
      model: current?.billing_model_id || 'hourly',
      config: { ...(current?.billing_config || {}) },
      effective_from: firstOfThisMonth(),
      note: '',
    });
  };

  const openCorrect = (row) => setForm({
    mode: 'correct',
    id: row.id,
    model: row.billing_model_id,
    config: { ...(row.billing_config || {}) },
    effective_from: row.effective_from,
    note: '',
  });

  const setCfg = (key, val) =>
    setForm((f) => ({ ...f, config: { ...f.config, [key]: val === '' ? null : parseFloat(val) } }));

  const save = async () => {
    if (!form) return;
    setBusy(true);
    const args = form.mode === 'correct'
      ? ['correct_client_billing_terms', {
          p_terms_id: form.id, p_model: form.model,
          p_config: form.config, p_note: form.note || null }]
      : ['change_client_billing_terms', {
          p_client_id: clientId, p_effective_from: form.effective_from,
          p_model: form.model, p_config: form.config, p_note: form.note || null }];
    const { error } = await supabase.rpc(args[0], args[1]);
    setBusy(false);
    if (error) { onError?.(error.message); return; }
    setForm(null);
    await load();
    onChanged?.();
  };

  const remove = async (row) => {
    setBusy(true);
    setConfirmRemove(null);
    const { error } = await supabase.rpc('delete_client_billing_terms', { p_terms_id: row.id });
    setBusy(false);
    if (error) { onError?.(error.message); return; }
    await load();
    onChanged?.();
  };

  const openSeason = () => {
    const current = rows.find((r) => statusOf(r, today) === 'current') || rows[rows.length - 1];
    const model = current?.billing_model_id || 'hourly';
    setSeason({
      startYear: new Date().getFullYear() + 1,
      years: 3,
      seasons: [
        { start_month: 6, model, config: {}, label: 'Summer' },
        { start_month: 9, model, config: { ...(current?.billing_config || {}) }, label: 'School year' },
      ],
    });
  };

  const setSeasonField = (idx, key, val) =>
    setSeason((s) => ({ ...s, seasons: s.seasons.map((x, i) => i === idx ? { ...x, [key]: val } : x) }));
  const setSeasonCfg = (idx, key, val) =>
    setSeason((s) => ({ ...s, seasons: s.seasons.map((x, i) =>
      i === idx ? { ...x, config: { ...x.config, [key]: val === '' ? null : parseFloat(val) } } : x) }));

  const saveSeason = async () => {
    if (!season) return;
    setBusy(true);
    const { error } = await supabase.rpc('generate_seasonal_billing_terms', {
      p_client_id: clientId,
      p_seasons: season.seasons,
      p_start_year: Number(season.startYear),
      p_years: Number(season.years),
    });
    setBusy(false);
    if (error) { onError?.(error.message); return; }
    setSeason(null);
    await load();
    onChanged?.();
  };

  const summarize = (row) => {
    const m = BILLING_MODELS[row.billing_model_id];
    return m ? m.summary(row.billing_config || {}) : '';
  };

  // A client whose last period is bounded stops having terms on that date.
  // Better to say so now than to have invoice generation discover it later.
  const last = rows[rows.length - 1];
  const coverageEnds = last && last.effective_to ? last.effective_to : null;

  const lbl = { ...(css?.lbl || {}) };
  const inp = { ...(css?.inp || {}) };

  // These read as plain text links when styled as ghost buttons, which made
  // three different actions look like incidental labels. Give them a real
  // outline so they are obviously clickable and obviously distinct.
  const actionBtn = (tone) => ({
    fontSize: '11px', fontWeight: 600, padding: '5px 11px', borderRadius: '6px',
    whiteSpace: 'nowrap', cursor: busy ? 'default' : 'pointer',
    border: `1px solid ${tone === 'accent' ? (C?.accent || '#00A0C8') : (C?.border || '#28395C')}`,
    background: 'transparent',
    color: tone === 'accent' ? (C?.accent || '#00A0C8') : (C?.text || C?.muted),
    opacity: busy ? 0.55 : 1,
  });

  return (
    <div style={{ borderTop: `1px solid rgba(30,39,54,0.5)`, paddingTop: '12px', marginTop: '10px' }}>
     <div style={{ maxWidth: '820px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px' }}>
        <div style={{ fontSize: '12px', fontWeight: 700, color: C?.textBright }}>Billing Terms</div>
        {!form && (
          <TKTooltip align="center" text="Use this when the price actually changed. The current rate is kept on record with an end date, and the new rate applies from the date you choose. Nothing you have already invoiced is affected.">
            <button style={actionBtn('accent')} onClick={openChange} disabled={busy}>
              Change rate…
            </button>
          </TKTooltip>
        )}
        {!form && !season && (
          <TKTooltip align="center" text="For a rate that alternates through the year — one rate over the summer, another for the rest, repeating annually. This writes out the actual periods for as many years as you choose; each one can be corrected or removed afterwards like any other.">
            <button style={actionBtn()} onClick={openSeason} disabled={busy}>
              Seasonal schedule…
            </button>
          </TKTooltip>
        )}
      </div>

      {loading && <div style={{ fontSize: '12px', color: C?.muted }}>Loading…</div>}

      {!loading && rows.length === 0 && (
        <div style={{ fontSize: '12px', color: C?.badgeAmber }}>
          No billing terms on record. This client cannot be invoiced from terms until one is added.
        </div>
      )}

      {!loading && rows.map((row) => {
        const st = statusOf(row, today);
        const end = lastCoveredDay(row.effective_to);
        return (
          <div key={row.id}
               style={{ display: 'flex', alignItems: 'flex-start', gap: '10px',
                        padding: '10px 12px', marginBottom: '8px', borderRadius: '8px',
                        // A neutral gray-blue tint rather than a theme token: applyTheme
                        // copies only a fixed set of keys onto C, and `metric` -- the
                        // token meant for raised surfaces -- is not one of them, so it
                        // is undefined at runtime. This reads correctly on both themes.
                        background: 'rgba(127,142,162,0.07)',
                        border: `1px solid ${C?.border || '#28395C'}`,
                        // Left edge carries the status, so the timeline is scannable
                        // without reading each label.
                        borderLeft: `3px solid ${
                          st === 'current'   ? (C?.badgeGreen || '#68d391')
                          : st === 'scheduled' ? (C?.badgeAmber || '#f6c26b')
                          : (C?.border || '#28395C')}`,
                        opacity: st === 'past' ? 0.62 : 1 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                <span style={{ ...(css?.badge?.(modelBadge(row.billing_model_id)) || {}), fontSize: '10px' }}>
                  {modelLabel(row.billing_model_id, 'shortLabel')}
                </span>
                <span style={{ fontSize: '12px', color: C?.textBright }}>{summarize(row)}</span>
                {st === 'current' && <span style={{ fontSize: '10px', color: C?.badgeGreen }}>● in effect</span>}
                {st === 'scheduled' && <span style={{ fontSize: '10px', color: C?.badgeAmber }}>scheduled</span>}
              </div>
              <div style={{ fontSize: '11px', color: C?.muted, marginTop: '2px' }}>
                {prettyDate(row.effective_from)} → {end ? prettyDate(end) : 'ongoing'}
                {row.note ? ` · ${row.note}` : ''}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '4px' }}>
              <TKTooltip align="left" text="Use this when the amount here is simply wrong — a typo, or the wrong figure entered. It fixes this period in place. The dates do not change and no new period is created.">
                <button style={actionBtn()} onClick={() => openCorrect(row)} disabled={busy}>Correct</button>
              </TKTooltip>
              {st === 'scheduled' && (
                <TKTooltip align="left" text="Deletes this upcoming period entirely, as if it had never been scheduled. The period before it carries on in its place. Only periods that have not started yet can be removed.">
                  <button style={actionBtn()} onClick={() => setConfirmRemove(row)} disabled={busy}>Remove</button>
                </TKTooltip>
              )}
            </div>
          </div>
        );
      })}

      {!loading && coverageEnds && (
        <div style={{ fontSize: '11px', color: C?.badgeAmber, marginTop: '8px' }}>
          Billing terms end {prettyDate(lastCoveredDay(coverageEnds))}. Add a following period before then,
          or invoices after that date will have no terms to price from.
        </div>
      )}

      {season && (() => {
        const total = season.seasons.length * Number(season.years || 0);
        const ordered = [...season.seasons].sort((a, b) => a.start_month - b.start_month);
        const firstM = ordered[0]?.start_month;
        const lastM  = ordered[ordered.length - 1]?.start_month;
        const endYear = Number(season.startYear) + Number(season.years || 1) - 1;
        return (
        <div style={{ marginTop: '10px', padding: '12px', borderRadius: '8px',
                      background: 'rgba(0,0,0,0.18)', border: `1px solid rgba(30,39,54,0.6)` }}>
          <div style={{ fontSize: '11px', color: C?.muted, marginBottom: '10px', lineHeight: 1.55 }}>
            Define each season once and choose how many years to lay down. This writes out the real
            periods — it does not store a rule — so every one of them can be corrected or removed
            individually afterwards.
          </div>

          {season.seasons.map((sn, i) => (
            <div key={i} style={{ marginBottom: '10px', padding: '10px', borderRadius: '6px',
                                  border: `1px solid ${C?.border}`, background: 'rgba(127,142,162,0.05)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <input style={{ ...inp, flex: 1 }} placeholder="Season name (e.g. Summer)"
                       value={sn.label} onChange={(e) => setSeasonField(i, 'label', e.target.value)} />
                {season.seasons.length > 2 && (
                  <button style={actionBtn()} disabled={busy}
                          onClick={() => setSeason((s) => ({ ...s, seasons: s.seasons.filter((_, j) => j !== i) }))}>
                    Remove
                  </button>
                )}
              </div>
              <div style={{ marginBottom: '8px' }}>
                <div style={lbl}>Starts</div>
                <select style={css?.sel} value={sn.start_month}
                        onChange={(e) => setSeasonField(i, 'start_month', Number(e.target.value))}>
                  {MONTH_NAMES.map((m, mi) => <option key={m} value={mi + 1}>{m} 1</option>)}
                </select>
              </div>
              <div style={{ marginBottom: '8px' }}>
                <div style={lbl}>Billing Model</div>
                <select style={css?.sel} value={sn.model}
                        onChange={(e) => { setSeasonField(i, 'model', e.target.value); setSeasonField(i, 'config', {}); }}>
                  {MODEL_ORDER.map((id) => (
                    <option key={id} value={id}>{BILLING_MODELS[id].selectLabel}</option>
                  ))}
                </select>
              </div>
              {(BILLING_MODELS[sn.model]?.fields || []).map((f) => (
                <div key={f.key} style={{ marginBottom: '8px' }}>
                  <div style={lbl}>{f.label}</div>
                  <input style={inp} type="number" value={sn.config?.[f.key] ?? ''}
                         onChange={(e) => setSeasonCfg(i, f.key, e.target.value)} />
                </div>
              ))}
            </div>
          ))}

          {season.seasons.length < 6 && (
            <button style={{ ...actionBtn(), marginBottom: '10px' }} disabled={busy}
                    onClick={() => setSeason((s) => ({ ...s, seasons: [...s.seasons,
                      { start_month: 1, model: s.seasons[0].model, config: {}, label: '' }] }))}>
              + Add a season
            </button>
          )}

          <div style={{ display: 'flex', gap: '10px', marginBottom: '10px', flexWrap: 'wrap' }}>
            <div>
              <div style={lbl}>First Year</div>
              <input style={{ ...inp, width: '110px' }} type="number" value={season.startYear}
                     onChange={(e) => setSeason((s) => ({ ...s, startYear: e.target.value }))} />
            </div>
            <div>
              <div style={lbl}>Repeat For</div>
              <input style={{ ...inp, width: '110px' }} type="number" min="1" max="20" value={season.years}
                     onChange={(e) => setSeason((s) => ({ ...s, years: e.target.value }))} />
              <div style={{ fontSize: '10px', color: C?.muted, marginTop: '3px' }}>years</div>
            </div>
          </div>

          {(() => {
            // A season left entirely blank generates $0 periods silently. $0 is
            // legitimate (pro bono, internal work) so this warns rather than
            // blocks -- but in a generated schedule a blank season is far more
            // likely to be an oversight than an intention.
            const blank = season.seasons.filter((sn) =>
              (BILLING_MODELS[sn.model]?.fields || []).every((f) => !Number(sn.config?.[f.key])));
            if (blank.length === 0) return null;
            return (
              <div style={{ fontSize: '11px', color: C?.badgeAmber, marginBottom: '10px', lineHeight: 1.5 }}>
                {blank.map((b) => b.label || 'One season').join(', ')}{' '}
                {blank.length === 1 ? 'has' : 'have'} no amount set — those periods would bill $0.
              </div>
            );
          })()}

          {total > 0 && firstM && (
            <div style={{ fontSize: '11px', color: C?.badgeBlue, marginBottom: '10px', lineHeight: 1.55 }}>
              Creates {total} period{total === 1 ? '' : 's'}, from {MONTH_NAMES[firstM - 1]} {season.startYear}
              {' '}through {MONTH_NAMES[lastM - 1]} {endYear}. After the last one, that rate simply continues
              until you add more — so the client is never left without a rate.
            </div>
          )}

          <div style={{ display: 'flex', gap: '8px' }}>
            <button style={{ ...(css?.btn?.('primary') || {}), fontSize: '12px' }}
                    onClick={saveSeason} disabled={busy}>
              {busy ? 'Creating…' : `Create ${total} Period${total === 1 ? '' : 's'}`}
            </button>
            <button style={{ ...(css?.btn?.('ghost') || {}), fontSize: '12px' }}
                    onClick={() => setSeason(null)} disabled={busy}>Cancel</button>
          </div>
        </div>
        );
      })()}

      {confirmRemove && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex',
                      alignItems: 'center', justifyContent: 'center', zIndex: 100000 }}
             onClick={() => setConfirmRemove(null)}>
          <div onClick={(e) => e.stopPropagation()}
               style={{ background: C?.surface, border: `1px solid ${C?.border}`, borderRadius: '12px',
                        padding: '28px', maxWidth: '420px', margin: '20px' }}>
            <div style={{ fontSize: '15px', fontWeight: 700, color: C?.danger, marginBottom: '8px' }}>
              Remove this billing period?
            </div>
            <div style={{ fontSize: '13px', color: C?.textDim || C?.muted, marginBottom: '18px', lineHeight: 1.55 }}>
              This will completely remove the scheduled period{' '}
              <strong style={{ color: C?.textBright }}>
                {BILLING_MODELS[confirmRemove.billing_model_id]
                  ? BILLING_MODELS[confirmRemove.billing_model_id].summary(confirmRemove.billing_config || {})
                  : modelLabel(confirmRemove.billing_model_id)}
              </strong>{' '}
              starting {prettyDate(confirmRemove.effective_from)}. The period before it will carry on in its
              place, so this client is never left without a rate. This cannot be undone.
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button style={{ ...(css?.btn?.() || {}), fontSize: '13px' }}
                      onClick={() => setConfirmRemove(null)} disabled={busy}>Cancel</button>
              <button style={{ ...(css?.btn?.('danger') || {}), fontSize: '13px' }}
                      onClick={() => remove(confirmRemove)} disabled={busy}>
                {busy ? 'Removing…' : 'Remove Period'}
              </button>
            </div>
          </div>
        </div>
      )}

      {form && (
        <div style={{ marginTop: '10px', padding: '10px', borderRadius: '6px',
                      background: 'rgba(0,0,0,0.18)', border: `1px solid rgba(30,39,54,0.6)` }}>
          <div style={{ fontSize: '11px', color: C?.muted, marginBottom: '8px' }}>
            {form.mode === 'correct'
              ? 'Correcting this period in place. Its dates do not change and no new period is created — use this when the old values were simply wrong.'
              : 'Opening a new billing period. Whatever period covers the date below will be closed on that date.'}
          </div>

          <div style={{ marginBottom: '8px' }}>
            <div style={lbl}>Billing Model</div>
            <select style={css?.sel} value={form.model}
                    onChange={(e) => setForm((f) => ({ ...f, model: e.target.value, config: {} }))}>
              {MODEL_ORDER.map((id) => (
                <option key={id} value={id}>{BILLING_MODELS[id].selectLabel}</option>
              ))}
            </select>
          </div>

          {(BILLING_MODELS[form.model]?.fields || []).map((f) => (
            <div key={f.key} style={{ marginBottom: '8px' }}>
              <div style={lbl}>{f.label}</div>
              <input style={inp} type="number" value={form.config?.[f.key] ?? ''}
                     onChange={(e) => setCfg(f.key, e.target.value)} />
            </div>
          ))}

          {form.mode === 'change' && (
            <div style={{ marginBottom: '8px' }}>
              <div style={lbl}>Effective From</div>
              <input style={inp} type="date" value={form.effective_from}
                     onChange={(e) => setForm((f) => ({ ...f, effective_from: e.target.value }))} />
              <div style={{ display: 'flex', gap: '6px', marginTop: '5px', flexWrap: 'wrap' }}>
                {[['1st of this month', firstOfThisMonth()],
                  ['Today', todayStr()],
                  ['1st of next month', firstOfNextMonth()]].map(([label, val]) => (
                  <button key={label}
                          style={{ ...(css?.btn?.('ghost') || {}), fontSize: '10px', padding: '3px 8px' }}
                          onClick={() => setForm((f) => ({ ...f, effective_from: val }))}>{label}</button>
                ))}
              </div>
            </div>
          )}

          <div style={{ marginBottom: '10px' }}>
            <div style={lbl}>Note <span style={{ color: C?.muted, fontWeight: 400 }}>(optional)</span></div>
            <input style={inp} placeholder="e.g. 2027 contract renewal" value={form.note}
                   onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} />
          </div>

          <div style={{ display: 'flex', gap: '8px' }}>
            <button style={{ ...(css?.btn?.('primary') || {}), fontSize: '12px' }} onClick={save} disabled={busy}>
              {busy ? 'Saving…' : form.mode === 'correct' ? 'Save Correction' : 'Add Period'}
            </button>
            <button style={{ ...(css?.btn?.('ghost') || {}), fontSize: '12px' }}
                    onClick={() => setForm(null)} disabled={busy}>Cancel</button>
          </div>
        </div>
      )}
     </div>
    </div>
  );
}
